/**
 * File System Access API — utilidades aisladas para ExportarDirectorio.
 * Sin dependencia de paneles SAT/FIEL ni de axios.
 */
import { BNT_ROOT_FOLDER } from "@/lib/exportDirectorioPaths";

export {
  isPickerPermissionError,
  isPickerUserAbort,
  supportsDirectoryPicker,
} from "@/lib/cfdiExportFolder";

async function getOrCreateDir(rootHandle, parts) {
  let current = rootHandle;
  for (const part of parts) {
    current = await current.getDirectoryHandle(part, { create: true });
  }
  return current;
}

/** Crea o devuelve la subcarpeta BNT_Visor_Fiscal bajo la carpeta raíz del usuario. */
export async function ensureBntRootDirectory(userRootHandle) {
  return userRootHandle.getDirectoryHandle(BNT_ROOT_FOLDER, { create: true });
}

/** Comprueba si existe un archivo por ruta relativa al handle dado. */
export async function fileHandleExists(rootHandle, relativePath) {
  const normalized = String(relativePath || "").replace(/\\/g, "/").replace(/^\/+/, "");
  const segments = normalized.split("/").filter(Boolean);
  if (!segments.length) return false;
  try {
    let current = rootHandle;
    for (let i = 0; i < segments.length - 1; i += 1) {
      current = await current.getDirectoryHandle(segments[i]);
    }
    await current.getFileHandle(segments[segments.length - 1]);
    return true;
  } catch {
    return false;
  }
}

/** Escribe un archivo con ruta relativa dentro del handle raíz. */
export async function writeRelativeFile(rootHandle, relativePath, blob) {
  const normalized = String(relativePath || "").replace(/\\/g, "/").replace(/^\/+/, "");
  const segments = normalized.split("/").filter(Boolean);
  if (!segments.length) throw new Error("Ruta relativa inválida");
  const fileName = segments.pop();
  const dirHandle = segments.length ? await getOrCreateDir(rootHandle, segments) : rootHandle;
  const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/**
 * Escribe solo XML del manifest en la jerarquía BNT_Visor_Fiscal/RFC/Emitidos|Recibidos/Año/Mes/.
 * @returns {{ writtenXml: number, skipped: number }}
 */
export async function writeManifestToDirectory({
  userRootHandle,
  manifest,
  clientId,
  clientRfc,
  mapItemPath,
  fetchXml,
  onProgress,
  signal,
}) {
  const items = manifest?.items || [];
  let writtenXml = 0;
  let skipped = 0;

  for (let i = 0; i < items.length; i += 1) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const item = items[i];
    if (typeof onProgress === "function") {
      onProgress({ current: i + 1, total: items.length, uuid: item.uuid });
    }
    if (!item?.id) {
      skipped += 1;
      continue;
    }
    try {
      const xmlBlob = await fetchXml(clientId, item.id, signal);
      if (!(xmlBlob instanceof Blob) || xmlBlob.size === 0) {
        skipped += 1;
        continue;
      }
      const relPath = mapItemPath(item, clientRfc);
      await writeRelativeFile(userRootHandle, relPath, xmlBlob);
      writtenXml += 1;
    } catch {
      skipped += 1;
    }
  }

  return { writtenXml, skipped };
}
